import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Alert,
	Box,
	Button,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	Spinner,
	Stack,
	Text,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import {
	Form,
	GetFieldValue,
	SelectField,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { SelectTermsAndConditions, XHR } from "../TermsAndConditions/List"
import { ITermsAndCondition } from "../TermsAndConditions"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import * as Validator from "yup"

type TInvoiceTemplateForm = {
	title: string
	terms_and_conditions: Array<ITermsAndCondition>
	default_hsn: string
	display_hsn: boolean
	display_place_of_supply: boolean
	show_price_breakup_settings: {
		display_line_items: boolean
		display_price_breakup: boolean
	}
	terms_section_settings: { title: string }
}

const validationSchema = validateFormValues(
	Validator.object().shape({
		title: Validator.string().required("Title is required"),
		terms_section_settings: Validator.object().shape({
			title: Validator.string().required("Terms section settings is required"),
		}),
	})
)

export function InvoiceTemplates() {
	const xhr = useXHR()
	const { xs } = useBreakpoints()
	const { hasPermission } = useCheckPermissions()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()

	const { data, error, isLoading, mutate } = useSWR(
		"invoice-templates",
		async () => {
			const [templateResp, featuredResp] = await Promise.all([
				xhr.get<{
					data: Array<Omit<TInvoiceTemplateForm, "terms_and_conditions">>
				}>("invoice-templates?include=use_fallback"),
				XHR(xhr).get({ featured_in: "invoices", limit: "all" }),
			])
			return {
				template: templateResp.data.data?.[0],
				termsAndConditions: featuredResp.data,
			}
		}
	)

	if (error && !data) {
		return (
			<Alert status="error">
				{error.message || "Something went wrong. Please try after sometime."}
			</Alert>
		)
	}

	if (isLoading || !data) {
		return <Spinner alignCenter padding="4" />
	}

	const { template, termsAndConditions } = data

	const initialValues: TInvoiceTemplateForm = {
		title: template?.title,
		default_hsn: template?.default_hsn || "",
		display_hsn: template?.display_hsn,
		display_place_of_supply: template?.display_place_of_supply,
		show_price_breakup_settings: {
			display_line_items:
				template?.show_price_breakup_settings?.display_line_items,
			display_price_breakup:
				template?.show_price_breakup_settings?.display_price_breakup,
		},
		terms_section_settings: {
			title: template?.terms_section_settings?.title,
		},
		terms_and_conditions: termsAndConditions,
	}

	return (
		<Stack gap="4">
			<Inline justifyContent="between" gap="4" alignItems="center">
				<Inline gap="4" alignItems="center">
					{xs ? null : (
						<Inline
							size="12"
							rounded="full"
							bgColor="default"
							borderWidth="1"
							alignItems="center"
							justifyContent="center"
							color="muted"
							fontFamily="mono"
						>
							PDF
						</Inline>
					)}
					<Stack gap="1">
						<Heading as="h3">Proforma Invoice Template Configurations</Heading>
						<Text>
							These configuration will be applied to Proforma Invoice only.
						</Text>
					</Stack>
				</Inline>
				{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<Box>
						<Button
							onClick={openDialog}
							level={!template ? "primary" : "secondary"}
							status="primary"
							size="sm"
						>
							<Icons.Pencil /> Edit
						</Button>
					</Box>
				) : null}
			</Inline>
			<Divider marginY="0" />

			<Box>
				{!template ? (
					<Box padding="4" bgColor="default" rounded="md" borderWidth="1">
						<Text>Using default Invoice template configurations.</Text>
					</Box>
				) : (
					<Stack gap="4">
						<SummaryItem label="Title Customization">
							{template.title || <NotAvailable />}
						</SummaryItem>
						<SummaryItem label="Show Place of Supply">
							{template.display_place_of_supply ? "Yes" : "No"}
						</SummaryItem>
						<SummaryItem label="Show Line Items">
							{template.show_price_breakup_settings?.display_line_items
								? "Yes"
								: "No"}
						</SummaryItem>
						<SummaryItem label="Show Price Breakup">
							{template.show_price_breakup_settings?.display_price_breakup
								? "Yes"
								: "No"}
						</SummaryItem>
						<SummaryItem label="Show HSN/SAC Number">
							{template.display_hsn ? "Yes" : "No"}
						</SummaryItem>
						<SummaryItem label="Default HSN/SAC Number">
							{template.default_hsn || <NotAvailable />}
						</SummaryItem>
						<SummaryItem label="Title for T&C Section">
							{template.terms_section_settings?.title || <NotAvailable />}
						</SummaryItem>
						<Inline flexWrap="wrap" gap="1">
							<Text as="span" fontWeight="semibold">
								Terms and Conditions Section
							</Text>
							:{" "}
							{termsAndConditions.length
								? joinAttributes(...termsAndConditions.map((item) => item.name))
								: "No Terms and Conditions selected"}
						</Inline>
					</Stack>
				)}
			</Box>

			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Update Proforma Invoice Template"
				lg
			>
				<Form<TInvoiceTemplateForm>
					initialValues={initialValues}
					validate={validationSchema}
					subscription={{ submitting: true }}
					onSubmit={withServerErrors(async (values) => {
						const {
							title,
							default_hsn,
							display_hsn,
							display_place_of_supply,
							show_price_breakup_settings,
							terms_section_settings,
						} = values
						await xhr.post("invoice-templates", {
							title,
							terms_section_settings,
							default_hsn: display_hsn ? default_hsn || "" : "",
							display_hsn: display_hsn ? 1 : 0,
							display_place_of_supply: display_place_of_supply ? 1 : 0,
							show_price_breakup_settings: {
								display_line_items:
									show_price_breakup_settings.display_line_items ? 1 : 0,
								display_price_breakup:
									show_price_breakup_settings.display_price_breakup ? 1 : 0,
							},
							terms_and_conditions: values.terms_and_conditions.map(
								(t) => t.id
							),
						})
						await mutate()
						closeDialog()
						showSnackbar("Successfully Changed")
					})}
				>
					{({ handleSubmit, submitting, form }) => (
						<form onSubmit={handleSubmit} noValidate>
							<Dialog.Body>
								<Stack gap="4">
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">Title Customization</Heading>
												<Text color="muted">
													Configure title settings like Draft Invoice, Estimate
													Invoice, Preliminary Invoice.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Component initialState={false}>
												{({ state: editing, setState }) =>
													!editing ? (
														<Stack gap="1">
															<Text fontWeight="semibold">Section Title</Text>
															<Inline
																borderWidth="1"
																rounded="md"
																paddingLeft="4"
																bgColor="subtle"
																gap="4"
																justifyContent="between"
																alignItems="center"
																maxWidth="sm"
															>
																<Text>{template.title}</Text>
																<Button
																	onClick={() => setState(true)}
																	level="tertiary"
																	title="Edit section title"
																>
																	<Icons.Pencil />
																</Button>
															</Inline>
														</Stack>
													) : (
														<TextInputField
															name="title"
															label="Section Title"
															type="text"
															placeholder="e.g Proforma Invoice"
															maxWidth="sm"
														/>
													)
												}
											</Component>
										</Col>
									</Grid>

									<Divider marginY="0" />
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">Place of Supply</Heading>
												<Text color="muted">
													Customize the settings of Place of Supply section
												</Text>
											</Stack>
										</Col>
										<Col>
											<SwitchInputField
												name="display_place_of_supply"
												label="Show Place of Supply"
											/>
										</Col>
									</Grid>

									<Divider marginY="0" />
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">
													Add Line Item/Price Breakup
												</Heading>
												<Text color="muted">
													Customize the settings of Add Line Item/Price Breakup
													section
												</Text>
											</Stack>
										</Col>
										<Col>
											<Stack gap="4">
												<SwitchInputField
													name="show_price_breakup_settings.display_line_items"
													label="Add Line Item"
													onChange={(value) => {
														const settings =
															form.getState().values.show_price_breakup_settings

														form.change("show_price_breakup_settings", {
															...settings,
															display_line_items: Boolean(value),
															display_price_breakup: value
																? Boolean(settings?.display_price_breakup)
																: false,
														})
													}}
												/>
												<GetFieldValue<
													boolean | undefined
												> name="show_price_breakup_settings.display_line_items">
													{({ value: addLineItems }) => (
														<SwitchInputField
															disabled={!addLineItems}
															name="show_price_breakup_settings.display_price_breakup"
															label="Show Price Breakup"
														/>
													)}
												</GetFieldValue>
											</Stack>
										</Col>
									</Grid>

									<Divider marginY="0" />
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">HSN/SAC</Heading>
												<Text color="muted">
													Customize the settings of HSN/SAC section
												</Text>
											</Stack>
										</Col>
										<Col>
											<Inline justifyContent="start" gap="4" alignItems="start">
												<SwitchInputField
													name="display_hsn"
													label="Show HSN/SAC"
												/>
												<GetFieldValue<boolean | undefined> name="display_hsn">
													{({ value }) =>
														value ? (
															<TextInputField
																label="Default HSN/SAC"
																name="default_hsn"
																type="text"
															/>
														) : null
													}
												</GetFieldValue>
											</Inline>
										</Col>
									</Grid>

									<Divider marginY="0" />
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">Title for TnC. Section</Heading>
												<Text color="muted">Customize the section title.</Text>
											</Stack>
										</Col>
										<Col>
											<TextInputField
												name="terms_section_settings.title"
												label="Section Title"
												type="text"
												placeholder="e.g Terms and Conditions"
												maxWidth="sm"
											/>
										</Col>
									</Grid>

									<Divider marginY="0" />
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">
													Terms and Conditions Selection
												</Heading>
												<Text color="muted">
													The selected terms and conditions will appear on the
													proforma invoice
												</Text>
											</Stack>
										</Col>
										<Col>
											<SelectField
												name="terms_and_conditions"
												label="Terms and Conditions"
												select={SelectTermsAndConditions}
												multiple
												fetchOnMount
												creatable
											/>
										</Col>
									</Grid>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Saving..." : "Save Details"}
								</Button>
								<Button disabled={submitting} onClick={closeDialog}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</Stack>
	)
}

function SummaryItem({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<Text>
			<Text as="span" fontWeight="semibold">
				{label}
			</Text>
			: {children}
		</Text>
	)
}

function NotAvailable() {
	return (
		<Text color="muted" as="span">
			N/A
		</Text>
	)
}
